import { Turbo } from "@hotwired/turbo-rails";

Turbo.StreamActions.replace_or_append = function () {
  if (this.targetElements.length !== 1) {
    console.error(
      `replace_or_append: expected 1 target element, got ${this.targetElements.length}`
    );
    return;
  }

  const target = this.targetElements[0];
  const children = [...this.templateContent.children];

  children.forEach((child) => {
    if (!child.id) {
      console.error("replace_or_append: every child element must have an id");
      return;
    }

    const existing = document.getElementById(child.id);

    if (existing) {
      existing.replaceWith(child);
    } else {
      target.append(child);
    }
  });
};

// Pending pre-reload snapshots, keyed by frame. Set when reload_frame fires and consumed by that
// frame's single turbo:frame-load / turbo:frame-missing listener — so a failed reload can't
// leak/accumulate listeners, and ordinary frame navigations (no pending snapshot) are left untouched.
// The snapshot doubles as the "this reload is ours" flag both handlers key off.
const ITEM_SELECTOR = "[data-reload-signature][id]";
const pendingReloadFlash = new WeakMap();
const flashListenerBound = new WeakSet();

// Reload a Turbo frame in place. Used to refresh the POS map when Mo edits Nexus out of band.
// The frame has no `src` until a filter is applied, so fall back to the current URL — which carries
// the viewer's filters because the filter dropdown navigates with turbo_action: "advance". Snapshot
// each item's data-reload-signature first so that, once the fresh content lands, we flash only the
// items whose signature changed (or are new) instead of the whole frame.
Turbo.StreamActions.reload_frame = function () {
  const frame = document.getElementById(this.target);
  if (!frame) return;
  prepareFrameReload(frame);
  if (!flashListenerBound.has(frame)) {
    flashListenerBound.add(frame);
    frame.addEventListener("turbo:frame-load", () => finishFrameReload(frame));
    frame.addEventListener("turbo:frame-missing", (event) => abandonFrameReload(frame, event));
  }
  scheduleFrameReload(frame);
};

// A bulk write broadcasts once per record — archiving forty voucher types, or the super-admin "Update
// display names" action rewriting every one of them — and each signal would otherwise cost every open
// page its own GET. Coalesce a burst into one reload. The snapshot taken before scheduling still
// describes the pre-burst DOM, because nothing reloads while the timer is pending.
export const RELOAD_DEBOUNCE_MS = 150;
const pendingReloadTimers = new WeakMap();

export function scheduleFrameReload(frame) {
  clearTimeout(pendingReloadTimers.get(frame));
  pendingReloadTimers.set(
    frame,
    setTimeout(() => {
      pendingReloadTimers.delete(frame);
      reloadFrameNow(frame);
    }, RELOAD_DEBOUNCE_MS)
  );
}

// A frame can declare its own reload URL (data-reload-src) for when the current URL can't be trusted —
// e.g. the Home page's pins frame, reloaded while Mo (which triggered it) has advanced the address bar
// onto a chat URL that lacks this frame. Otherwise reload the current URL (filter-carrying frames like
// the POS map rely on this). Re-setting src to its current value is a no-op, so reload() when unchanged.
export function reloadFrameNow(frame) {
  const url = frame.dataset.reloadSrc;
  if (url) {
    if (frame.getAttribute("src") === url) frame.reload();
    else frame.src = url;
  } else if (frame.src) {
    frame.reload();
  } else {
    frame.src = window.location.href;
  }
}

// Snapshot every item's signature so the fresh content can be diffed against it.
export function prepareFrameReload(frame) {
  pendingReloadFlash.set(frame, reloadSignatures(frame));
}

// Fresh content landed — flash whatever changed. A frame load we didn't trigger (e.g. the map's filter
// form navigating this same frame) has no snapshot, and is left alone.
export function finishFrameReload(frame) {
  const before = pendingReloadFlash.get(frame);
  if (!before) return;
  pendingReloadFlash.delete(frame);
  flashChangedItems(frame, before);
}

// The reload landed on a page with no matching frame — e.g. a POS read failed and the controller
// rendered the frameless 502 "unavailable" template. Turbo's default is to replace the frame with
// "Content missing", wiping a list the viewer never asked to reload; keep the stale content instead.
// Strictly limited to our own background reloads: these listeners stay bound for the life of the frame,
// and a user-initiated navigation that goes missing must still surface Turbo's failure rather than
// silently leaving stale content behind.
export function abandonFrameReload(frame, event) {
  if (!pendingReloadFlash.has(frame)) return;
  pendingReloadFlash.delete(frame);
  event.preventDefault();
}

export function reloadSignatures(frame) {
  const signatures = new Map();
  frame.querySelectorAll(ITEM_SELECTOR).forEach((el) => {
    signatures.set(el.id, el.dataset.reloadSignature);
  });
  return signatures;
}

export function flashChangedItems(frame, before) {
  const added = [];
  const changed = [];
  frame.querySelectorAll(ITEM_SELECTOR).forEach((el) => {
    if (before.get(el.id) === el.dataset.reloadSignature) return; // unchanged
    (before.has(el.id) ? changed : added).push(el);
    el.classList.remove("reload-flash");
    void el.offsetWidth; // reflow so the animation restarts if a flash is still in flight
    el.classList.add("reload-flash");
    el.addEventListener("animationend", () => el.classList.remove("reload-flash"), { once: true });
  });
  if (frame.dataset.reloadScrollIntoView) scrollToFlashed(added, changed);
}

// The list pages opt in: their rows sit in a scrollable table, so a row Mo just added can be far enough
// down the alphabet to flash off-screen. The map doesn't (its columns are laid out in full).
// Prefer a genuinely new row; failing that only follow a single edited row, since a bulk change would
// otherwise scroll to whichever row happens to sort first, which tells the viewer nothing.
function scrollToFlashed(added, changed) {
  const target = added[0] || (changed.length === 1 ? changed[0] : null);
  if (!target) return;

  // "nearest" is a no-op when the row is already fully visible, so this never nudges the page for nothing.
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  target.scrollIntoView({ block: "nearest", behavior: reduced ? "auto" : "smooth" });
}

// The page's own backing record was deleted out of band (e.g. Mo/another admin removed a voucher
// batch while its show page was open). reload_frame's "keep stale content" fallback is wrong here —
// there's no fresh content to reload, the URL itself is dead — so navigate the viewer away instead.
Turbo.StreamActions.redirect_to = function () {
  const url = this.getAttribute("url");
  if (url) Turbo.visit(url);
};

Turbo.StreamActions.set_mo_description = function () {
  const description = this.getAttribute("description");
  if (!description) return;

  this.targetElements.forEach((target) => {
    target
      .querySelectorAll("[data-massimo-describe-listener]")
      .forEach((el) =>
        el.dispatchEvent(new CustomEvent("massimo:describe", { detail: { description } }))
      );
  });
};
